import {
  computed,
  getCurrentInstance,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
} from 'vue'
import { isDescendantOf } from '../utils/misc'
import { useRollbar } from './rollbar'

export type Position = 'top' | 'bottom' | 'left' | 'right'
export type Alignment = 'start' | 'center' | 'end'

// this block here is an update loop that repositions any overlay panel on screen based
// on their respective anchor/target element's position
interface ElementUpdateData {
  cachedPosition: { top: number; left: number }
  update(pos: { top: number; left: number }): void
  observer: IntersectionObserver
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const elementsDataMap = new Map<any, ElementUpdateData>()
let isRunningUpdateLoop = false
let startMs: number
function updateLoop(ms: number) {
  // auto stop the loop if we don't have any element to reposition
  if (!elementsDataMap.size) {
    isRunningUpdateLoop = false
    return
  }

  // keeping it on max 60 fps, so not to overdo ourselves
  if (!startMs) {
    startMs = ms
  } else if (ms - startMs < 16) {
    requestAnimationFrame(updateLoop)
    return
  }
  startMs = ms

  for (const data of elementsDataMap.values()) {
    if (!data) continue
    data.update(data.cachedPosition)
  }
  requestAnimationFrame(updateLoop)
}
function runUpdateLoop() {
  if (isRunningUpdateLoop) return

  isRunningUpdateLoop = true
  updateLoop(0)
}

const openedOverlays = new Set<{ hide(): void }>()
let isBlocked = false
export function useOverlayController() {
  function block() {
    isBlocked = true
  }
  function unblock() {
    isBlocked = false
  }
  function closeAll() {
    for (const overlay of openedOverlays) {
      overlay.hide()
    }
  }

  return {
    block,
    unblock,
    closeAll,
  }
}

interface Options {
  position?: Position
  alignment?: Alignment
  type?: 'hover' | 'click' | 'manual'
  autoReposition?: boolean
  passive?: boolean
  warpZone?: string
  targeting?: 'children' | 'sibling'
}

export function useOverlayApi(id: string, options: Options) {
  const {
    position = 'top',
    alignment = 'center',
    type = 'hover',
    passive = false,
    warpZone = '.tooltip-warp-zone',
    autoReposition = false,
    targeting = 'children',
  } = options
  const contentClass = `${id}-content`
  const overlayElement = ref<HTMLElement | null>(null)
  const overridePosition = ref<Position | null>(null)
  const isVisible = ref(false)
  const targetElement = ref<HTMLElement | null>(null)

  const inst = getCurrentInstance()

  const translateX = ref('')
  const translateY = ref('')

  const resizeObserver = new ResizeObserver(() => {
    requestAnimationFrame(() => {
      adjustPosition()
    })
  })

  const coords = reactive({
    top: 'auto',
    bottom: 'auto',
    left: 'auto',
    right: 'auto',
  })
  const arrowCoords = reactive({
    top: 'auto',
    bottom: 'auto',
    left: 'auto',
    right: 'auto',
  })

  const padding = 8 // 8px or .5rem

  const style = computed(() => {
    return {
      ...coords,
      transform: [
        translateX.value && `translateX(${translateX.value})`,
        translateY.value && `translateY(${translateY.value})`,
      ]
        .filter(Boolean)
        .join(' '),
    }
  })
  const arrowStyle = computed(() => {
    return {
      ...arrowCoords,
      transform: [
        position === 'top' || position === 'bottom' ? 'translateX(-50%)' : '',
        position === 'left' || position === 'right' ? 'translateY(-50%)' : '',
        'rotate(45deg)',
      ]
        .filter(Boolean)
        .join(' '),
    }
  })

  const positionClass = computed(
    () => `p-${overridePosition.value ?? position}`,
  )

  function resetPosition() {
    coords.top = 'auto'
    coords.bottom = 'auto'
    coords.left = 'auto'
    coords.right = 'auto'
    translateX.value = ''
    translateY.value = ''
    arrowCoords.top = 'auto'
    arrowCoords.bottom = 'auto'
    arrowCoords.left = 'auto'
    arrowCoords.right = 'auto'
  }

  function setPosition() {
    if (!targetElement.value) return
    const pos = overridePosition.value || position

    const rect = targetElement.value.getBoundingClientRect()
    if (pos === 'top') {
      coords.top = `${rect.top - padding}px`
      translateY.value = '-100%'
      arrowCoords.top = `${rect.top - padding * 2}px`
      if (alignment === 'start') {
        coords.left = `${rect.left - 12}px`
        translateX.value = ''
      } else if (alignment === 'end') {
        coords.left = `${rect.right + 12}px`
        translateX.value = '-100%'
      } else {
        coords.left = `${rect.left + rect.width / 2}px`
        translateX.value = '-50%'
      }
      arrowCoords.left = `${rect.left + rect.width / 2}px`
    } else if (pos === 'bottom') {
      coords.top = `${rect.bottom + padding}px`
      translateY.value = ''
      arrowCoords.top = `${rect.bottom + padding / 2}px`
      if (alignment === 'start') {
        coords.left = `${rect.left - 12}px`
        translateX.value = ''
      } else if (alignment === 'end') {
        coords.left = `${rect.right + 12}px`
        translateX.value = '-100%'
      } else {
        coords.left = `${rect.left + rect.width / 2}px`
        translateX.value = '-50%'
      }
      arrowCoords.left = `${rect.left + rect.width / 2}px`
    } else if (pos === 'left') {
      coords.left = `${rect.left - padding}px`
      translateX.value = '-100%'
      arrowCoords.left = `${rect.left - padding * 2}px`
      if (alignment === 'start') {
        coords.top = `${rect.top}px`
        translateY.value = ''
      } else if (alignment === 'end') {
        coords.top = `${rect.bottom}px`
        translateY.value = '-100%'
      } else {
        coords.top = `${rect.top + rect.height / 2}px`
        translateY.value = '-50%'
      }
      arrowCoords.top = `${rect.top + rect.height / 2}px`
    } else if (pos === 'right') {
      coords.left = `${rect.right + padding}px`
      translateX.value = ''
      arrowCoords.left = `${rect.right + padding / 2}px`
      if (alignment === 'start') {
        coords.top = `${rect.top}px`
        translateY.value = ''
      } else if (alignment === 'end') {
        coords.top = `${rect.bottom}px`
        translateY.value = '-100%'
      } else {
        coords.top = `${rect.top + rect.height / 2}px`
        translateY.value = '-50%'
      }
      arrowCoords.top = `${rect.top + rect.height / 2}px`
    }
  }

  const marginX = 8
  const marginY = 16
  function adjustPosition() {
    if (!overlayElement.value) return
    const { top, bottom, left, right, height } =
      overlayElement.value.getBoundingClientRect()
    const { clientWidth, clientHeight } = document.body.querySelector(warpZone)!

    if (!height) {
      setTimeout(adjustPosition, 100)
      return
    }

    const pos = overridePosition.value ?? position
    if (left < marginX) {
      if (pos === 'left') {
        isVisible.value = false
        resetPosition()
        overridePosition.value = 'right'
        setPosition()
      } else {
        coords.left = '.5rem'
        coords.right = 'auto'
        translateX.value = ''
      }
    } else if (right >= clientWidth - marginX) {
      if (pos === 'right') {
        isVisible.value = false
        resetPosition()
        overridePosition.value = 'left'
        setPosition()
      } else {
        coords.right = '.5rem'
        coords.left = 'auto'
        translateX.value = ''
      }
    }

    if (top < marginY) {
      if (pos === 'top' && height < 100) {
        isVisible.value = false
        resetPosition()
        overridePosition.value = 'bottom'
        setPosition()
      } else {
        coords.top = '1rem'
        coords.bottom = 'auto'
        translateY.value = ''
      }
    } else if (bottom >= clientHeight - marginY) {
      if (pos === 'bottom' && height < 100) {
        isVisible.value = false
        resetPosition()
        overridePosition.value = 'top'
        setPosition()
      } else {
        coords.bottom = '1rem'
        coords.top = 'auto'
        translateY.value = ''
      }
    }

    // in case we re-adjusted the position, by means of override, we need to re-trigger the v-if
    // logic for the slide animation to apply the proper position class (i.e. p-left/p-right etc)
    if (!isVisible.value) {
      setTimeout(() => {
        isVisible.value = true

        nextTick(() => {
          adjustPosition()
        })
      }, 0)
    }
  }

  function reposition() {
    setPosition()
    nextTick(() => adjustPosition())
  }

  const api = { hide }

  function show() {
    // Possibly due to a race condition, sometimes the show() is called after the targetElement
    // has been destroyed
    if (!targetElement.value) return

    setPosition()
    isVisible.value = true

    // necessary to fix a particularly annoying Safari bug
    // @ts-ignore
    inst.__isVisible = isVisible.value

    setTimeout(() => {
      reposition()
      if (overlayElement.value) resizeObserver.observe(overlayElement.value)
    }, 0)

    if (type === 'manual' || autoReposition) {
      // here we add an IntersectionObserver on the targetElement to understand if it's effectively
      // visible on screen, in which case the overlay should also be visible, otherwise we hide it
      // Helpful if the targetElement is inside a panel with overflow: hidden/scroll boundaries,
      // and we don't want to show a overlay hanging onto nothing
      const o = new IntersectionObserver(entries => {
        isVisible.value = entries[0].isIntersecting
        // necessary to fix a particularly annoying Safari bug
        // @ts-ignore
        inst.__isVisible = isVisible.value
      })
      // and here we cache some data necessary for the repositioning loop
      elementsDataMap.set(inst, {
        cachedPosition: { top: 0, left: 0 },
        update(cachedPosition: { top: number; left: number }) {
          // @ts-ignore
          if (!inst.__isVisible) {
            if (type === 'manual') return

            hide()
            return
          }

          const rect = targetElement.value?.getBoundingClientRect()
          // we avoid repositioning the element if the target element hasn't moved
          if (
            !rect ||
            (rect.top === cachedPosition.top &&
              rect.left === cachedPosition.left)
          ) {
            return
          }
          cachedPosition.top = rect.top
          cachedPosition.left = rect.left
          isVisible.value = false
          setPosition()
          adjustPosition()
          isVisible.value = true
        },
        observer: o,
      })
      o.observe(targetElement.value!)
      // we also run the update loop that will reposition the overlay if necessary
      runUpdateLoop()
      if (!autoReposition) return
    }

    if (!passive) {
      if (type === 'hover') {
        window.addEventListener('scroll', hide)
      } else {
        window.addEventListener('scroll', reposition)
      }
      window.addEventListener('click', hide, true)
      openedOverlays.add(api)
    }
  }
  function hide(e?: Event) {
    if (overlayElement.value) resizeObserver.unobserve(overlayElement.value)

    if (
      type !== 'hover' &&
      e?.target &&
      e.target !== document &&
      (overlayElement.value === e?.target ||
        isDescendantOf(e.target as HTMLElement, contentClass, {
          includeSelf: true,
        }) ||
        targetElement.value === e.target ||
        targetElement.value?.contains(e.target as Node) ||
        // disabling "click to hide" behavior if we blocked the overlay with the
        // `useOverlayController().block()` functionality
        isBlocked ||
        // a bit hacky, but this condition keeps StellaMenus opened if they are in the lower warp
        // zone (meaning they are beneath modals in the layer stack) and the click is originating
        // from a StellaModal – this helps in situations where a menu triggers the opening of a
        // modal and waits for a submit of that modal to then emit a certain event to the menu
        // consumer; if the menu were to close itself when the user is interfacing with the modal
        // the emit chain would break and we don't want that to happen
        (warpZone === '.menu-lower-warp-zone' &&
          isDescendantOf(e.target as HTMLElement, 'StellaModal', {
            includeSelf: true,
          })))
    ) {
      return
    }

    isVisible.value = false
    overridePosition.value = null
    resetPosition()

    if (type === 'manual' || autoReposition) {
      // when we dismiss the overlay we also disconnect the observer and remove the cached data
      // which will also stop the update loop in case this was the only data left in the cache map.
      // we also do this in the deinit() function just to be sure.
      elementsDataMap.get(inst)?.observer.disconnect()
      elementsDataMap.delete(inst)
      if (!autoReposition) return
    }

    if (type === 'hover') {
      window.removeEventListener('scroll', hide)
    } else {
      window.removeEventListener('scroll', reposition)
    }
    window.removeEventListener('click', hide)
    openedOverlays.delete(api)
  }

  function toggle(e: Event) {
    e?.stopImmediatePropagation()
    if (isVisible.value) hide()
    else show()
  }

  function init() {
    if (!targetElement.value) return
    if (type === 'manual') return
    if (type === 'hover') {
      targetElement.value.addEventListener('mouseenter', show)
      targetElement.value.addEventListener('mouseleave', hide)
    } else {
      // type === 'click'
      targetElement.value.addEventListener('click', toggle, true)
    }
  }
  function deinit() {
    if (!targetElement.value) return

    if (type === 'manual' || autoReposition) {
      elementsDataMap.get(inst)?.observer.disconnect()
      elementsDataMap.delete(inst)
    }
    if (type !== 'manual') {
      window.removeEventListener('click', hide)
      if (type === 'hover') {
        targetElement.value.removeEventListener('mouseenter', show)
        targetElement.value.removeEventListener('mouseleave', hide)
        window.removeEventListener('scroll', hide)
      } else {
        // type === 'click'
        targetElement.value.removeEventListener('click', toggle)
        window.removeEventListener('scroll', reposition)
      }
    }

    targetElement.value = null
  }

  const rollbar = useRollbar()
  onMounted(() => {
    setTimeout(async () => {
      try {
        // magic!
        if (targeting === 'children') {
          // @ts-ignore
          const targetComponent = inst?.subTree.dynamicChildren[0].children[0]
          targetElement.value = targetComponent.el
          if (
            // if the element is of type "text"
            targetElement.value?.nodeType === 3 ||
            // or of type "comment"
            targetElement.value?.nodeType === 8
          ) {
            // we select the next element and hope for the better
            // @ts-ignore
            targetElement.value = targetElement.value.nextElementSibling
          }
          // if we still don't have it, we might be targeting an async component
          // in which case we need to await the async loader to end before we could target its
          // DOM structure
          if (!targetElement.value) {
            if (targetComponent.type.name === 'AsyncComponentWrapper') {
              await targetComponent.type.__asyncLoader()
              setTimeout(() => {
                targetElement.value = targetComponent.el
                if (
                  targetElement.value?.nodeType === 3 ||
                  targetElement.value?.nodeType === 8
                ) {
                  // @ts-ignore
                  targetElement.value = targetElement.value.nextElementSibling
                }
                init()
              }, 100)
            }
          } else {
            init()
          }
        } else {
          targetElement.value = inst?.subTree.el?.nextElementSibling
          init()
        }
      } catch (e) {
        const { message, stack } = e as Error

        rollbar.error(
          `Overlay error: Could not find the trigger element for "${id}"`,
          {
            jsErrorMessage: message,
            jsErrorStack: stack,
          },
        )
      }
    }, 100)
  })
  onBeforeUnmount(() => deinit())

  return {
    positionClass,
    style,
    arrowStyle,
    targetElement,
    isVisible,
    overlayElement,
    show,
    hide,
    reposition,
    contentClass,
  }
}
